import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { buildOrgUrl, getOrgSlugFromHostname } from '@/core/domain';
import { acceptInvitation } from '@/core/invitation';
import { setToken } from '@/core/token';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { useFormik } from 'formik';
import { Fingerprint } from 'lucide-react';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { InputWithError } from './InputWithError';

export const LoginForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const me = useMe();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigateToOrg = (organizationSlugs?: string[]) => {
    const currentSlug = getOrgSlugFromHostname();
    // If already on a valid org subdomain that the user has access to, stay there
    if (currentSlug && organizationSlugs?.includes(currentSlug)) {
      navigate('/');
      return;
    }
    // If the user has orgs, redirect to the first one's subdomain
    if (organizationSlugs && organizationSlugs.length > 0) {
      window.location.href = buildOrgUrl(organizationSlugs[0]);
      return;
    }
    // No orgs — go to onboarding
    navigate('/auth/onboarding/org');
  };

  const setParams = (params: Record<string, string | null | undefined | number>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (String(value)) newParams.set(key, String(value));
      else newParams.delete(key);
    });
    setSearchParams(newParams);
  };

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    setError(null);
    const toastId = toast.loading('Authenticating with passkey...');
    try {
      // Get authentication options (discoverable credentials, no email needed)
      const optionsRes = await fetch(`${import.meta.env.VITE_ENDPOINT}/passkey/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const optionsData = await optionsRes.json();
      if (!optionsRes.ok) throw new Error(optionsData.error || 'Failed to get passkey options');

      const credential = await startAuthentication({ optionsJSON: optionsData.options });

      const verifyRes = await fetch(`${import.meta.env.VITE_ENDPOINT}/passkey/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, challenge: optionsData.challenge, challengeSignature: optionsData.challengeSignature }),
      });
      const result = await verifyRes.json();
      if (!result.success) throw new Error(result.error || 'Passkey verification failed');

      setToken(result.token);
      me.loadMe();
      toast.success('Login successful!', { id: toastId });
      const invitation = searchParams.get('invitation');
      if (invitation) navigate(await acceptInvitation(invitation));
      else navigateToOrg(result.organizationSlugs);
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        toast.dismiss(toastId);
      } else {
        const msg = err.message || 'Passkey login failed';
        setError(msg);
        toast.error(msg, { id: toastId });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get values from URL params with defaults
  const loginMode = (searchParams.get('mode') as 'otp' | 'password') || 'otp';
  const otpSecret = searchParams.get('secret');
  const otpSent = !!otpSecret; // OTP is sent if secret exists

  // 2FA state
  const twoFactorSecret = searchParams.get('twoFactorSecret');
  const twoFactorType = searchParams.get('twoFactorType') as 'SMS' | 'AUTHENTICATOR' | null;
  const twoFactorRequired = !!twoFactorSecret || twoFactorType === 'AUTHENTICATOR';

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      otp: '',
      twoFactorCode: '',
    },
    validate: (values) => {
      const errors: any = {};

      if (twoFactorRequired && !values.twoFactorCode) {
        errors.twoFactorCode = 'Please enter the verification code';
      }
      if (twoFactorRequired && values.twoFactorCode && values.twoFactorCode.length !== 6) {
        errors.twoFactorCode = 'Code must be 6 characters';
      }

      if (!twoFactorRequired && loginMode === 'otp' && otpSecret && !values.otp) {
        errors.otp = 'Please enter the one-time passcode';
      }

      if (!twoFactorRequired && loginMode === 'otp' && otpSecret && values.otp && values.otp.length !== 6) {
        errors.otp = 'Passcode must be 6 characters';
      }

      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      // Handle 2FA verification
      if (twoFactorRequired) {
        const toastId = toast.loading('Verifying 2FA code...', {
          description: 'Please wait while we verify your code',
        });

        try {
          const captchaToken = await executeRecaptcha?.('verify_2fa');
          const res = await fetch(`${import.meta.env.VITE_ENDPOINT}/verify-2fa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },

            body: JSON.stringify({
              twoFactorSecret: twoFactorSecret || undefined,
              twoFactorType: twoFactorType || 'SMS',
              email: values.email || searchParams.get('email') || undefined,
              code: values.twoFactorCode,
              captchaToken,
            }),
          });

          const data = await res.json();

          if (!res.ok) {
            formik.setFieldError('twoFactorCode', data.error || 'Invalid code');
            throw new Error(data.error || 'Invalid code');
          }

          if (data.success && data.token) {
            setToken(data.token);
            me.loadMe();
            toast.success('Login successful!', {
              description: 'Welcome back to your GIS account',
              id: toastId,
            });
            const invitation = searchParams.get('invitation');
            if (invitation) navigate(await acceptInvitation(invitation));
            else navigateToOrg(data.organizationSlugs);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Invalid code';
          setError(errorMessage);
          toast.error('Verification failed', {
            description: errorMessage,
            id: toastId,
          });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (loginMode === 'otp') {
        if (!otpSecret) {
          // Send OTP
          const toastId = toast.loading('Sending passcode...', {
            description: 'Please wait while we send your one-time passcode',
          });

          try {
            const captchaToken = await executeRecaptcha?.('login');

            const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
  
              body: JSON.stringify({
                email: values.email,
                captchaToken,
                method: 'OTP',
              }),
            });

            const data = await response.json();

            if (!response.ok) {
              if (data.error === 'EMAIL_NOT_VERIFIED' && data.secret) {
                toast.info('Please verify your email first', { id: toastId });
                const params = new URLSearchParams({ secret: data.secret, email: data.email || values.email });
                navigate(`/auth/verify?${params.toString()}`);
                return;
              }
              throw new Error(data.error || 'Failed to send passcode');
            }

            if (data.success) {
              // Store the secret in URL params (otpSent is determined by secret presence)
              setParams({
                mode: 'otp',
                secret: data.secret,
              });
              toast.success('Passcode sent!', {
                description: 'Check your email for the one-time passcode',
                id: toastId,
              });
            } else {
              throw new Error('Invalid response from server');
            }
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to send passcode';
            setError(errorMessage);
            toast.error('Failed to send passcode', {
              description: errorMessage,
              id: toastId,
            });
          } finally {
            setIsLoading(false);
          }
        } else {
          // Verify OTP
          const toastId = toast.loading('Verifying passcode...', {
            description: 'Please wait while we verify your passcode',
          });

          try {
            const captchaToken = await executeRecaptcha?.('login');
            const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
  
              body: JSON.stringify({
                email: values.email,
                otp: values.otp,
                secret: otpSecret,
                captchaToken,
                method: 'OTP',
              }),
            });

            const data = await response.json();

            if (!response.ok) {
              // Set formik error for OTP field
              formik.setFieldError('otp', data.error || 'Invalid passcode');
              throw new Error(data.error || 'Invalid passcode');
            }

            if (data.success && data.twoFactorRequired) {
              const isAuthenticator = data.twoFactorType === 'AUTHENTICATOR';
              toast.info('Two-factor authentication required', {
                description: isAuthenticator
                  ? 'Enter the code from your authenticator app'
                  : 'A verification code has been sent to your phone',
                id: toastId,
              });
              setParams({
                twoFactorSecret: data.twoFactorSecret || null,
                twoFactorType: data.twoFactorType || 'SMS',
                email: values.email,
              });
            } else if (data.success && data.token) {
              setToken(data.token);
              me.loadMe();
              toast.success('Login successful!', {
                description: 'Welcome back to your GIS account',
                id: toastId,
              });
              const invitation = searchParams.get('invitation');
              if (invitation) navigate(await acceptInvitation(invitation));
              else navigateToOrg(data.organizationSlugs);
            } else {
              throw new Error('Invalid response from server');
            }
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Invalid passcode';
            setError(errorMessage);
            toast.error('Invalid passcode', {
              description: errorMessage,
              id: toastId,
            });
          } finally {
            setIsLoading(false);
          }
        }
      } else {
        // Password login
        const toastId = toast.loading('Logging in...', {
          description: 'Please wait while we authenticate you',
        });

        try {
          const captchaToken = await executeRecaptcha?.('login');
          const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },

            body: JSON.stringify({
              email: values.email,
              password: values.password,
              captchaToken,
              method: 'PASSWORD',
            }),
          });

          const data = await response.json();

          if (!response.ok) {
            if (data.error === 'EMAIL_NOT_VERIFIED' && data.secret) {
              toast.info('Please verify your email first', { id: toastId });
              const params = new URLSearchParams({ secret: data.secret, email: data.email || values.email });
              navigate(`/auth/verify?${params.toString()}`);
              return;
            }
            throw new Error(data.error || 'Login failed');
          }

          if (data.success && data.twoFactorRequired) {
            const isAuthenticator = data.twoFactorType === 'AUTHENTICATOR';
            toast.info('Two-factor authentication required', {
              description: isAuthenticator
                ? 'Enter the code from your authenticator app'
                : 'A verification code has been sent to your phone',
              id: toastId,
            });
            setParams({
              twoFactorSecret: data.twoFactorSecret || null,
              twoFactorType: data.twoFactorType || 'SMS',
              email: values.email,
            });
          } else if (data.success && data.token) {
            setToken(data.token);
            me.loadMe();
            toast.success('Login successful!', {
              description: 'Welcome back to your GIS account',
              id: toastId,
            });
            const invitation = searchParams.get('invitation');
            if (invitation) navigate(await acceptInvitation(invitation));
            else navigateToOrg(data.organizationSlugs);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Login failed';
          setError(errorMessage);
          toast.error('Login failed', {
            description: errorMessage,
            id: toastId,
          });
        } finally {
          setIsLoading(false);
        }
      }
    },
  });

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <img src={logo} alt='Logo' className='size-16 mb-6' />
                <h1 className='text-2xl font-bold'>Welcome back</h1>
                <p className='text-muted-foreground text-balance'>
                  {twoFactorRequired
                    ? twoFactorType === 'AUTHENTICATOR'
                      ? 'Enter the code from your authenticator app'
                      : 'Enter the verification code sent to your phone'
                    : loginMode === 'otp'
                      ? otpSent
                        ? 'Enter your one-time passcode'
                        : 'Login to your GIS account'
                      : 'Login to your GIS account'}
                </p>
                {error && (
                  <div className='mt-4 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-md'>
                    <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
                  </div>
                )}
              </div>
              {twoFactorRequired ? (
                <div className='grid gap-2'>
                  <Label>Verification Code</Label>
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                    value={formik.values.twoFactorCode}
                    onChange={(value) => {
                      formik.setFieldValue('twoFactorCode', value);
                      if (formik.errors.twoFactorCode) {
                        formik.setFieldError('twoFactorCode', '');
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') formik.handleSubmit(); }}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  {formik.touched.twoFactorCode && formik.errors.twoFactorCode && (
                    <p className='text-sm text-destructive'>{formik.errors.twoFactorCode}</p>
                  )}
                </div>
              ) : (
              <>
              <InputWithError
                label='Email'
                id='email'
                name='email'
                type='email'
                placeholder='m@example.com'
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email ? formik.errors.email : undefined}
                touched={formik.touched.email}
                required
                disabled={!!otpSecret && loginMode === 'otp'}
              />

              {loginMode === 'otp' && otpSecret && (
                <div className='grid gap-2'>
                  <Label>One-time Passcode</Label>
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                    value={formik.values.otp}
                    onChange={(value) => {
                      formik.setFieldValue('otp', value);
                      if (formik.errors.otp) {
                        formik.setFieldError('otp', '');
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') formik.handleSubmit(); }}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  {formik.touched.otp && formik.errors.otp && (
                    <p className='text-sm text-destructive'>{formik.errors.otp}</p>
                  )}
                </div>
              )}

              {loginMode === 'password' && (
                <div className='grid gap-2'>
                  <div className='flex items-center'>
                    <Label htmlFor='password'>Password</Label>
                    <Link to='/auth/forgot' className='ml-auto text-sm underline-offset-2 hover:underline' tabIndex={1}>
                      Forgot your password?
                    </Link>
                  </div>
                  <InputWithError
                    id='password'
                    name='password'
                    type='password'
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password ? formik.errors.password : undefined}
                    touched={formik.touched.password}
                    required
                    tabIndex={0}
                  />
                </div>
              )}
              </>
              )}
              <div className='flex flex-col gap-2'>
                <Button type='submit' className='w-full' disabled={isLoading}>
                  {isLoading
                    ? twoFactorRequired
                      ? 'Verifying...'
                      : loginMode === 'otp'
                        ? otpSecret
                          ? 'Verifying...'
                          : 'Sending...'
                        : 'Logging in...'
                    : twoFactorRequired
                      ? 'Verify code'
                      : loginMode === 'otp'
                        ? otpSecret
                          ? 'Verify passcode'
                          : 'Send passcode'
                        : 'Login'}
                </Button>
                {!twoFactorRequired && (
                  <div className='flex gap-2'>
                    {loginMode === 'otp' ? (
                      <Button
                        type='button'
                        variant='link'
                        className='flex-1'
                        onClick={() => {
                          setParams({ mode: 'password' });
                          formik.resetForm({ values: { email: formik.values.email, password: '', otp: '', twoFactorCode: '' } });
                          setError(null);
                        }}
                        disabled={isLoading}>
                        Log In Using Password
                      </Button>
                    ) : (
                      <Button
                        type='button'
                        variant='link'
                        className='flex-1'
                        onClick={() => {
                          setParams({ mode: 'otp' });
                          formik.resetForm();
                          setError(null);
                        }}
                        disabled={isLoading}>
                        Use One-time passcode
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {!twoFactorRequired && (
                <div className='text-center text-sm'>
                  Don{"'"}t have an account?{' '}
                  <Link to='/auth/signup' className='underline underline-offset-4 hover:text-primary'>
                    Sign up
                  </Link>
                </div>
              )}

              {!twoFactorRequired && browserSupportsWebAuthn() && (
                <>
                  <div className='after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t'>
                    <span className='bg-card text-muted-foreground relative z-10 px-2'>Or continue with</span>
                  </div>
                  <Button
                    type='button'
                    variant='outline'
                    className='w-full'
                    onClick={handlePasskeyLogin}
                    disabled={isLoading}>
                    <Fingerprint className='size-4 mr-2' />
                    Sign in with Passkey
                  </Button>
                </>
              )}
            </div>
          </form>
          <div className='bg-muted relative hidden md:block'>
            <img
              src='/placeholder.svg'
              alt='Image'
              className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
            />
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <a href='#'>Terms of Service</a> and <a href='#'>Privacy Policy</a>.
      </div>
    </div>
  );
};
